import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Loader from "@/components/Loader";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";
import ForceSetup2FA from "@/pages/ForceSetup2FA";
import NotFound from "@/pages/NotFound";

import Dashboard from "@/pages/admin/Dashboard";
import DeadlinesPage from "@/pages/admin/DeadlinesPage";
import ClientsPage from "@/pages/admin/ClientsPage";
import OnboardClient from "@/pages/admin/OnboardClient";
import ClientDetail from "@/pages/admin/ClientDetail";
import Practice from "@/pages/admin/Practice";
import SecurityPage from "@/pages/SecurityPage";

import ClientHome from "@/pages/client/ClientHome";
import ClientDocuments from "@/pages/client/ClientDocuments";
import ClientMessages from "@/pages/client/ClientMessages";
import ClientReports from "@/pages/client/ClientReports";
import ClientInvoices from "@/pages/client/ClientInvoices";
import ClientAccount from "@/pages/client/ClientAccount";

function RoleRoute({ allow, children }) {
  const { user } = useAuth();
  if (!allow.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function HomeDispatch() {
  const { user } = useAuth();
  return user.role === "client" ? <ClientHome /> : <Dashboard />;
}

function ProtectedGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ChangePassword forced />;
  if (user.needs_2fa_setup) return <ForceSetup2FA />;
  return <Layout />;
}

function Shell() {
  const { user, loading } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={loading ? <Loader /> : user ? <Navigate to="/" replace /> : <Login />} />
      <Route element={<ProtectedGate />}>
        <Route path="/" element={<HomeDispatch />} />
        <Route path="/deadlines" element={<RoleRoute allow={["admin", "staff"]}><DeadlinesPage /></RoleRoute>} />
        <Route path="/clients" element={<RoleRoute allow={["admin", "staff"]}><ClientsPage /></RoleRoute>} />
        <Route path="/clients/new" element={<RoleRoute allow={["admin"]}><OnboardClient /></RoleRoute>} />
        <Route path="/clients/:id" element={<RoleRoute allow={["admin", "staff"]}><ClientDetail /></RoleRoute>} />
        <Route path="/practice" element={<RoleRoute allow={["admin"]}><Practice /></RoleRoute>} />
        <Route path="/security" element={<RoleRoute allow={["admin", "staff"]}><SecurityPage /></RoleRoute>} />
        <Route path="/documents" element={<RoleRoute allow={["client"]}><ClientDocuments /></RoleRoute>} />
        <Route path="/messages" element={<RoleRoute allow={["client"]}><ClientMessages /></RoleRoute>} />
        <Route path="/reports" element={<RoleRoute allow={["client"]}><ClientReports /></RoleRoute>} />
        <Route path="/invoices" element={<RoleRoute allow={["client"]}><ClientInvoices /></RoleRoute>} />
        <Route path="/account" element={<RoleRoute allow={["client"]}><ClientAccount /></RoleRoute>} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Shell />
        <Toaster position="top-center" theme="dark" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
